import { Box, Dialog, Divider, IconButton, Stack, Tooltip, Typography } from "@mui/material"
import { Section } from "components/controls/section"
import { AuditLogResultWithId } from "interface/auditLog"
import { useCallback, useMemo, useState } from "react"
import { CloseIcon, NextIcon, PreviousIcon } from "shared/icons"
import { useKeyBind } from "shared/keyBind"
import { dateTimeStampFormat } from "shared/util"

type AuditLogPreviewDialogProps = {
  list?: AuditLogResultWithId[]
  log: AuditLogResultWithId
  onClose: () => void
}
export const AuditLogPreviewDialog = ({
  list,
  log,
  onClose
}: AuditLogPreviewDialogProps) => {
  const [currentId, setCurrentId] = useState<number | null>(log.id)
  const {
    current,
    prev,
    next,
  } = useMemo(
    () => {
      if (!list) return { current: log }
      const index = list?.findIndex(log => log.id === currentId) ?? -1
      return {
        current: (list && index >= 0) ? list[index] : null,
        next: (list && index >= 0 && index < (list.length - 1)) ? list[index + 1] : null,
        prev: (list && index > 0) ? list[index - 1] : null
      }
    },
    [list, currentId, log]
  )
  const openPrev = useCallback(
    () => {
      if (prev?.id) setCurrentId(prev.id)
    },
    [prev, setCurrentId]
  )
  const openNext = useCallback(
    () => {
      if (next?.id) setCurrentId(next.id)
    },
    [next, setCurrentId]
  )
  useKeyBind({ key: "PageUp", onKeyDown: openPrev })
  useKeyBind({ key: "PageDown", onKeyDown: openNext })
  return (
    <Dialog
      open
      fullWidth
      maxWidth='md'
      onClose={() => onClose()}
    >
      <Box>
        <Stack direction="column" sx={{ height: '100%', boxSizing: 'border-box' }}>
          <Stack direction='column' padding={1}>
            <Stack direction='row' spacing={1} justifyContent='flex-start' alignItems='flex-start'>
              <Stack direction='column' flexGrow={1}>
                <Stack direction='column' padding={1}>
                  <Typography variant='h5' component='div'>監査証跡ログ詳細</Typography>
                </Stack>
              </Stack>
              <IconButton disabled={prev === null} onClick={openPrev}><PreviousIcon/></IconButton>
              <IconButton disabled={next === null} onClick={openNext}><NextIcon/></IconButton>
              <Tooltip title='閉じる'><IconButton onClick={onClose}><CloseIcon/></IconButton></Tooltip>
            </Stack>
          </Stack>
          <Divider/>
          
       		<Stack direction='column' padding={1} flexGrow={1} overflow='auto'>
            {current && (
              <>
                <Section label="日時">
                  <Typography variant='body1'>{dateTimeStampFormat(current.logTime)}</Typography>
                </Section>
                <Section label="操作者">
                  <Typography variant='body1'>{current.operatorName}</Typography>
                </Section>
                {current.operatorId !== current.userId && (
                  <Section label="ユーザー名">
                    <Typography variant='body1'>{current.userName}</Typography>
                  </Section>
                )}
                <Section label="操作">
                  <Typography variant='body1'>{current.actionName}</Typography>
                </Section>
                {current.searchTitle && (
                  <Section label="分析">
                    <Typography variant='body1'>{current.searchTitle}</Typography>
                  </Section>
                )}
                {current.detail && (
                  <Section label="詳細">
                    <Typography variant='body1'>{current.detail}</Typography>
                  </Section>
                )}
                {current.clientIp && (
                  <Section label="IPアドレス">
                    <Typography variant='body1'>{current.clientIp}</Typography>
                  </Section>
                )}
                {(current.xForwardedFor && current.xForwardedFor.length > 0) && (
                  <Section label="x-Forwarded-For">
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {current.xForwardedFor.map((xff, i) => (
                        <Typography variant='body1' key={`x-forward-for-${i}`}>{xff}</Typography>
                      ))}
                    </Stack>
                  </Section>
                )}
                {(current.userAgent && current.userAgent.length > 0) && (
                  <Section label="x-Forwarded-For">
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {current.userAgent.map((ua, i) => (
                        <Typography variant='body1' key={`user-agent-${i}`}>{ua}</Typography>
                      ))}
                    </Stack>
                  </Section>
                )}
              </>
            )}
          </Stack>       
        </Stack>
      </Box>
    </Dialog>
  )
}